import { AgGridReact } from "ag-grid-react";
import { MouseEvent, useCallback, useEffect, useMemo, useRef } from "react";
import { aggridHeaderClass, ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { CellEditingStoppedEvent, ColDef, PostSortRowsParams } from "ag-grid-community";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useRowSelectionHandler, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter} from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell  } from "src/utils/grid/gridStyleUtils";
import { formatDate, validateString } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import { PRD2010_R01Dto } from "./Dto_PRD2010_R01";
import { usePRD2010_R01Store, usePRD2010_R01Store_GridCalendar } from "./Store_PRD2010_R01";
import { getItemPopupList } from "src/apis/itemApi";
import {
	headerNames_PRD2010_R01,
	restoreOriginalValues,
	handleEditingStopped as handleEditingStoppedUtil,
	handleGridCalendar as handleGridCalendarUtil,
	afterCalendarConfirmed as afterCalendarConfirmedUtil,
	handleGridCalendarChange as handleGridCalendarChangeUtil,
} from './Util_PRD2010_R01';
import { getWorkplaceChangePatch, usePRD2010_R01CellChange } from "./hooks/PRD2010_R01CellChange";
import { usePRD2010_R01Modal } from "./hooks/usePRD2010_R01Modal";
import { formatLocalDate } from "src/utils/date/dateUtils";
import { addHours } from "date-fns";
import { changeWorkplaceColumn, CommonCodeColumn, CompCdColumn, getCommonCodeColumnOptions, getCompCdColumnOptions, getWorkPlaceColumnOptions, WorkPlaceColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PRD2010_R01-grid-column-state';


const ITEM_EDITABLE_COLUMNS = ['itemCd', 'orderQty'];

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	// hoveredRowIndex: number | null;
	// setHoveredRowIndex: (hoveredRowIndex: number | null) => void;
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<PRD2010_R01Dto> | null>;
	defaultPRD2010_R01Row: PRD2010_R01Dto;
}

export const Table_PRD2010_R01 = (props: Props) => {

	const { 
		// hoveredRowIndex, setHoveredRowIndex,
		clickable, setClickable,
		gridRef,
		defaultPRD2010_R01Row,
	} = props;

    const { 
		PRD2010_R01List, setPRD2010_R01List,
		insertPRD2010_R01List,
		removePRD2010_R01List,
		updatePRD2010_R01List,
		selectedPRD2010_R01List, setSelectedPRD2010_R01List, removeSelectedPRD2010_R01List, addSelectedPRD2010_R01List, initSelectedPRD2010_R01List, allSelectedPRD2010_R01List,
		deletedPRD2010_R01List, removeDeletedPRD2010_R01List, addDeletedPRD2010_R01List,
        columnState, setColumnState,
		originalPRD2010_R01List, setOriginalPRD2010_R01List,
		isDisabled,
		modalVisible, setModalVisible,
		modalValueIndex, setModalValueIndex,
		modalInstructions, setModalInstructions,
		scrollTop, setScrollTop,
	} = usePRD2010_R01Store();

    const { compCdList, jobTeamList, orgCdList, ordTypeList, workProcessList, workPlaceList, unitList, employeeCdList } = useCommonCodeStore();

	const workPlaceListToUse = useMemo(
		() => workPlaceList.filter((opt) => opt.useYn === "Y" && opt.orderYn === "Y"),
		[workPlaceList],
	);
	
	// 달력 팝업 노출 여부 상태
	const { PRD2010_R01GridCalendarState, setPRD2010_R01GridCalendarState, initPRD2010_R01GridCalendarState } = usePRD2010_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate, gridCalendarTrigger } = PRD2010_R01GridCalendarState;
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성
	const isRowDraggingRef = useRef(false);

    //const gridRef = useRef<AgGridReact>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD2010_R01List,
		defaultGridData: defaultPRD2010_R01Row,
		selectedRow: selectedPRD2010_R01List,
		originalGridData: originalPRD2010_R01List,
		updateRow: (index: number, data: any) => updatePRD2010_R01List(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['basOrderItemNo', 'customNm', 'productOrderNo', 'orderEmpNm', 'workProcess', 'itemNm', 'itemSpec', 'itemUnit', 'resultQty'],
		insertedDisabledCells: [''],
		commonDisabledCells: ['basOrderNo',],
		
		pointerCells: ['compCd', 'orderDt', 'orderType', 'workplaceCd', 'orderQty', 'jobTeam', 'orderEmp', 'productQty', 'badQty', 'acceptQty', 'workProcess', 'itemCd', 'itemNm', 'itemSpec', 'itemUnit', 'orderQty'],
		insertedPointerCells: ['basOrderNo', 'basOrderItemNo', 'customNm', ],
		commonPointerCells: [''],
		
		alignLeftCells: ['compCd','workProcess', 'orderEmp']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	
	const basOrderDisabled = useCallback((params: any) => {
		// 수주번호 행을 편집할때 사용
		// 작업지시번호가 있을때 편집 불가
		if(validateString(params.data?.productOrderNo)) {
			return false;
		}
		return true;
	}, []);

	const itemDisabled = useCallback((params: any) => {
		if(params.data?.inserted === false && validateString(params.data?.basOrderNo)) {
			return false;
		}
		return true;
	}, []);

	const isEditable = useCallback((params: any) => {
		// 하단 합계(pinned) 행은 항상 편집 불가
		if (params.node?.rowPinned) return false;
		return !isDisabled(params.data) || params.data?.inserted === true;
	}, [isDisabled]);

	const isDynamicDisabledCell = useCallback((params: any) => {
		if (!params.data) return false;
		if (params.node?.rowPinned) return false;

		const colId = params.colDef.colId || params.colDef.field;

		if (ITEM_EDITABLE_COLUMNS.includes(colId)) {
			return !(itemDisabled(params) && isEditable(params));
		}

		return false;
	}, [itemDisabled, isEditable]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<PRD2010_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedPRD2010_R01List,
			deletedList: deletedPRD2010_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});

		const baseDisabledCellRule = baseRules['disabled-cell'];
		const basePointerCursorRule = baseRules['pointer-cursor'];

		return {
			...defaultColDef,
			cellClassRules: {
				...baseRules,
				'disabled-cell': (params: any) => baseDisabledCellRule(params) || isDynamicDisabledCell(params),
				'pointer-cursor': (params: any) => basePointerCursorRule(params) && !isDynamicDisabledCell(params),
			},
		}; 
	}, [
		selectedPRD2010_R01List, deletedPRD2010_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		isDynamicDisabledCell,
	]);
	
	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: PRD2010_R01List,
			rowData: PRD2010_R01List[rowIndex],
			insertRow: insertPRD2010_R01List,
			defaultRowData: defaultPRD2010_R01Row,
			setClickable
		});
	}, [PRD2010_R01List, insertPRD2010_R01List, defaultPRD2010_R01Row, setClickable]);

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['basOrderNo', 'compCd', 'orderType', 'workplaceCd', 'jobTeam', 'orderEmp', 'itemCd', 'orderQty', 'remark'];

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<PRD2010_R01Dto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const handleEditingStopped = useCallback((e: CellEditingStoppedEvent<PRD2010_R01Dto>) =>
		handleEditingStoppedUtil(e, setPRD2010_R01GridCalendarState),
	[setPRD2010_R01GridCalendarState]);

	const onInstructionsModal = useCallback((data: PRD2010_R01Dto) => {
		setModalVisible(true);
		setModalValueIndex(data.valueIndex);
		setModalInstructions(data.instructions);
	}, [setModalValueIndex, setModalVisible]);

	const baseColumnDefs: ColDef<PRD2010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: PRD2010_R01Dto) => renderIndexCell(
				params, 
				(innerParams: PRD2010_R01Dto) => getRowStyleFlags(
					innerParams,
					{
						deletedList: deletedPRD2010_R01List,
						selectedList: selectedPRD2010_R01List,
						dataList: PRD2010_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					PRD2010_R01List, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removePRD2010_R01List, 
					(valueIndex) => addDeletedPRD2010_R01List([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '작업지시번호', colId: 'productOrderNo', field: 'productOrderNo', width: 150, 
			editable: false,
		},
		{
			headerName: '수주번호', colId: 'basOrderNo', field: 'basOrderNo', width: 150, 
			editable: (params: any) => basOrderDisabled(params) && isEditable(params),
		},
		{
			headerName: '주문항목번호', colId: 'basOrderItemNo', field: 'basOrderItemNo', width: 150, 
			editable: false,
		},
		{
			headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 90, 
			editable: false,
		},
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			...CompCdColumn.options(compCdList, isEditable, 
				CompCdColumn.change(updatePRD2010_R01List, {
					getPatch: () => ({
						workplaceCd: defaultPRD2010_R01Row.workplaceCd || "",
						workProcess: defaultPRD2010_R01Row.workProcess || "",
					}),
				}),
				handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '작업일자', colId: 'orderDt', field: 'orderDt', width: 100, 
			editable: isEditable,
			valueFormatter: (params: any) => params.data?.orderDt ? formatDate(new Date(params.data?.orderDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '지시구분', colId: 'orderType', field: 'orderType', width: 100, 
			...CommonCodeColumn.options(ordTypeList, isEditable, CommonCodeColumn.change(updatePRD2010_R01List), handleGridDropDownCommitAfter),
		},
		{
			headerName: '작업공정', colId: 'workplaceCd', field: 'workplaceCd', width: 100, 
			...WorkPlaceColumn.options(workPlaceListToUse, isEditable,
				(params) => params.data?.compCd,
				WorkPlaceColumn.change(updatePRD2010_R01List, {
					getPatch: ({ newValue }) => getWorkplaceChangePatch(workPlaceListToUse, employeeCdList, newValue),
				}),
				handleGridDropDownCommitAfter
			),
		},
		{
			headerName: '공정유형', colId: 'workProcess', field: 'workProcess', width: 100, 
			...CommonCodeColumn.options(workProcessList, false, CommonCodeColumn.change(updatePRD2010_R01List), handleGridDropDownCommitAfter),
		},
		{
			headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam', width: 100, 
			...CommonCodeColumn.options(jobTeamList, isEditable, CommonCodeColumn.change(updatePRD2010_R01List), handleGridDropDownCommitAfter),
		},
		{
			headerName: '작업자', colId: 'orderEmp', field: 'orderEmp', width: 100, 
			editable: isEditable,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '작업자(명)', colId: 'orderEmpNm', field: 'orderEmpNm', width: 100, 
			editable: false,
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 100, 
			editable: (params: any) => itemDisabled(params) && isEditable(params),
			pasteable: false,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 100, 
			editable: false,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 100, 
			editable: false,
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 100, 
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updatePRD2010_R01List), handleGridDropDownCommitAfter),
		},
		{
			headerName: '수량', colId: 'orderQty', field: 'orderQty', width: 80, 
			editable: (params: any) => itemDisabled(params) && isEditable(params),
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '생산량', colId: 'resultQty', field: 'resultQty', width: 80, 
			editable: false,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '지시사항', colId: 'instructions', field: 'instructions', width: 120, 
			editable: false,
			cellRenderer: (params: any) => {
				// 하단 합계 행(pinned row)인 경우 렌더링 하지 않음
				if (params.node.rowPinned) return null;

				return (
					<div className="w-full h-full flex items-center justify-center">
						<button
							className="w-full h-full flex items-center justify-center bg-gray-300 text-white text-[18px] rounded hover:bg-gray-600 border border-gray-600"
							onClick={(e) => {
								e.stopPropagation(); // 행 클릭 이벤트 전파 방지
								onInstructionsModal(params.data);
							}}
						>
							입력
						</button>
					</div>
				);
			}
		},
		// {
		// 	headerName: '마감여부', colId: 'closeTag', field: 'closeTag', width: 100, 
		// 	editable: false,
		// },
		

		// ...IndexParameterCell(),

	], [
		deletedPRD2010_R01List, PRD2010_R01List, orgCdList, compCdList, workPlaceListToUse, employeeCdList, jobTeamList, ordTypeList, workProcessList, handleGridDropDownCommitAfter, defaultPRD2010_R01Row,
	]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<PRD2010_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PRD2010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames: headerNames_PRD2010_R01,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, columnState, headerNames_PRD2010_R01]);

	const { settingCodeSearchModalProps } = usePRD2010_R01Modal({
		PRD2010_R01List,
		updatePRD2010_R01List,
		originalPRD2010_R01List,
		gridRef,
	});

	const { handleCellChange } = usePRD2010_R01CellChange({
		updatePRD2010_R01List,
		originalPRD2010_R01List,
		gridRef,
		settingCodeSearchModalProps,
	});

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD2010_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

    const handleRowClick = useRowSelectionHandler({
        dataList: PRD2010_R01List,
        idKey: 'valueIndex',
        selectedList: selectedPRD2010_R01List,
        deletedList: deletedPRD2010_R01List,
        addSelected: addSelectedPRD2010_R01List,
        removeSelected: removeSelectedPRD2010_R01List,
        clickable, setClickable
    });

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = ['basOrderNo', 'basOrderItemNo', 'compCd', 'jobTeam', 'orderType', 'workplaceCd', 'workProcess', 'orderEmp', 'itemCd', 'itemSpec', 'itemUnit', 'orderQty', 'remark'];
	const cellClickActions: CustomCellClickAction<PRD2010_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'productOrderNo',
			action: (e, foundData) => {
				if(foundData.inserted) {
					showWarning('작업지시번호는 자동 생성됩니다.');
				}				
			}
		},
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
		{
			validate: (field, data) => field === 'workplaceCd',
			action: (e, foundData) => {
				const validateWorkplace = workPlaceList.filter(item => item.compCd === foundData.compCd);
				if(validateWorkplace.length > 0) {
					e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
				}else{
					e.api.stopEditing();
					showWarning('작업공정이 존재하지 않습니다.');
					
				}
			}
		},
		{
			validate: (field, data) => field === 'orderDt',
			action: createCalendarCellClickHandler('orderDt', PRD2010_R01GridCalendarState, setPRD2010_R01GridCalendarState),
		},
	], [PRD2010_R01GridCalendarState, createCalendarCellClickHandler]);
	const handleCellClick = CreateCellClickHandler(PRD2010_R01List, 'valueIndex', cellClickActions);

	//더블클릭 액션
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD2010_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				const colDef = baseColumnDefs.find(col => col.field === field);
				if (!colDef) return false;
				
				// editable이 함수인 경우 (isEditable 등) 실행해서 판별, 불리언이면 그 값 그대로 사용
				const isCellEditable = typeof colDef.editable === 'function' 
					? colDef.editable({ data } as any) 
					: !!colDef.editable;
					
				return !isCellEditable;
			},
			action: (e, foundData) => {
				onInstructionsModal(foundData);
			}
		},
		{
			validate: (field) => field === 'basOrderNo' || field === 'basOrderItemNo' || field === 'customNm',
			action: (e, foundData) => {
				if(!isEditable({ data: foundData })) return;

				// 편집 중인 값 또는 저장된 값 가져오기 (stopEditing 없이)
				const editingCells = gridRef.current?.api?.getEditingCells();
				let cellValue = '';
				
				if (editingCells && editingCells.length > 0) {
					// 현재 편집 중이면 편집 중인 값 가져오기
					const cellEditor = gridRef.current?.api?.getCellEditorInstances()[0];
					if (cellEditor && (cellEditor as any).getValue) {
						cellValue = (cellEditor as any).getValue() || '';
					}
				} 
				
				settingCodeSearchModalProps('수주번호', cellValue, false, 'basOrderNo', foundData, foundData.valueIndex!);
			}
		},
		{
			validate: (field) => field === 'orderEmp',
			action: (e, foundData) => {
				if(!isEditable({ data: foundData })) return;

				// 편집 중인 값 또는 저장된 값 가져오기 (stopEditing 없이)
				const editingCells = gridRef.current?.api?.getEditingCells();
				let cellValue = '';
				
				if (editingCells && editingCells.length > 0) {
					// 현재 편집 중이면 편집 중인 값 가져오기
					const cellEditor = gridRef.current?.api?.getCellEditorInstances()[0];
					if (cellEditor && (cellEditor as any).getValue) {
						cellValue = (cellEditor as any).getValue() || '';
					}
				} 
				
				settingCodeSearchModalProps('작업자', cellValue, false, 'orderEmp', foundData, foundData.valueIndex!);
			}
		},
		{
			validate: (field) => (field === 'itemCd' || field === 'itemNm' || field === 'itemSpec' || field === 'itemUnit'),
			action: (e, foundData) => {
				if(!isEditable({ data: foundData })) return;

				// 편집 중인 값 또는 저장된 값 가져오기 (stopEditing 없이)
				const editingCells = gridRef.current?.api?.getEditingCells();
				let cellValue = '';
				
				if (editingCells && editingCells.length > 0) {
					// 현재 편집 중이면 편집 중인 값 가져오기
					const cellEditor = gridRef.current?.api?.getCellEditorInstances()[0];
					if (cellEditor && (cellEditor as any).getValue) {
						cellValue = (cellEditor as any).getValue() || '';
					}
				} 
				
				settingCodeSearchModalProps('품목', cellValue, false, 'itemCd', foundData, foundData.valueIndex!);
			}
		},
	], [settingCodeSearchModalProps, PRD2010_R01List, isEditable]);
	
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD2010_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<PRD2010_R01Dto>) => {
		if (isRowDraggingRef.current) return;
		postSortHandler(params, PRD2010_R01List, originalPRD2010_R01List, setPRD2010_R01List);
	}, [PRD2010_R01List, originalPRD2010_R01List, ]);

	// const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<PRD2010_R01Dto>) => {
	// 	// 드래그 중에는 postSortRows에 의한 강제 정렬을 막아 드래그가 눈에 보이도록 함
	// 	if (isRowDraggingRef.current) return;
	// 	postSortHandler(params, PRD2010_R01List, originalPRD2010_R01List, setPRD2010_R01List);
	// }, [PRD2010_R01List, originalPRD2010_R01List, ]);

	const pinnedBottomRow = useMemo(() => ([
		{
			rowIndex: '계',
			productOrderNo: PRD2010_R01List.length.toLocaleString() + "건",
			orderQty: PRD2010_R01List.reduce((sum, account) => sum + (deletedPRD2010_R01List.includes(account.productOrderNo) ? 0 : Number(account.orderQty) || 0), 0).toLocaleString(),
			resultQty: PRD2010_R01List.reduce((sum, account) => sum + (deletedPRD2010_R01List.includes(account.productOrderNo) ? 0 : Number(account.resultQty) || 0), 0).toLocaleString(),
			editable: false,
		}
	]), [PRD2010_R01List]);

	const DtModifiedFunction = useMemo(() => {
		return (date: Date, valueIndex: number) => {
			const originRow = originalPRD2010_R01List.find(item => item.valueIndex === valueIndex);
			if(originRow) {
				if(formatDate(originRow.orderDt) === formatDate(date)) {
					return false;
				}
			}
			return true;
		}
	}, [originalPRD2010_R01List]);

	const handleGridCalendar = useCallback(
		(pending: number) => {
			const latestList = usePRD2010_R01Store.getState().PRD2010_R01List;
			handleGridCalendarUtil(
				pending,
				gridRef,
				gridWrapperRef,
				latestList,
				setPRD2010_R01GridCalendarState,
			);
		},
	[gridRef, gridWrapperRef, setPRD2010_R01GridCalendarState]);

	const afterCalendarConfirmed = useCallback((rowIdx: number, field: string) => 
		afterCalendarConfirmedUtil(rowIdx, field, gridRef), [gridRef]);

	const handleGridCalendarChange = useCallback(
		(date: Date | undefined, rowIndex: number | null, targetField: string | null) => {
			const latestList = usePRD2010_R01Store.getState().PRD2010_R01List;
			handleGridCalendarChangeUtil(
				date,
				rowIndex,
				targetField,
				latestList,
				DtModifiedFunction,
				updatePRD2010_R01List,
			);
		},
		[DtModifiedFunction, updatePRD2010_R01List],
	);

	useEffect(() => {
		if (gridCalendarTrigger == null) return;
		const pending = gridCalendarTrigger;
		const tick = window.setTimeout(() => handleGridCalendar(pending), 0);
		return () => window.clearTimeout(tick);
	}, [gridCalendarTrigger, handleGridCalendar]);

    return (
			<GridContextWrapper<PRD2010_R01Dto>
				enableCellCalculation
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				setPaddedGridData={setPRD2010_R01List}
				initSelected={initSelectedPRD2010_R01List}
				defaultRowData={defaultPRD2010_R01Row}
				allSelected={allSelectedPRD2010_R01List}
				baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<PRD2010_R01Dto>
					ref={gridRef}
					rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
					columnDefs={columnDefs}		
					defaultColDef={cellCssDefaultColDef}


					rowClassRules={createRowClassRules<PRD2010_R01Dto>({
						idKey: 'valueIndex',
						selectedList: selectedPRD2010_R01List,
						deletedList: deletedPRD2010_R01List,
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellEditingStarted={(p) => { /* console.log('edit start', p.rowIndex, p.colDef.field, p.data?.valueIndex); */ }}
					onCellValueChanged={handleCellChange}
					onCellFocused={(p) => {
						const colId = typeof p.column === 'string' ? p.column : p.column?.getColId();
						/* console.log('cell focused', p.rowIndex, colId, p.rowPinned); */
					}}
					pinnedBottomRowData={pinnedBottomRow}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
					onRowDragEnter={() => { isRowDraggingRef.current = true; }}
                    onRowDragEnd={(e: any) => {
                        const api = e.api;
                        // 정렬 해제하여 사용자가 정한 순서를 유지
                        if (api.applyColumnState) {
                            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
                        }
                        const reordered: PRD2010_R01Dto[] = [];
                        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
                            if (!node.data) return;
                            reordered.push({
                                ...node.data,
                                orderIndex: idx + 1,
                                orderIndexSeq: 0,
                            });
                        });
                        setPRD2010_R01List(reordered);
						isRowDraggingRef.current = false;
                    }}

					onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
					// Grid_Select에 공통 로직 처리를 위한 props 전달
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setColumnState}				//컬럼 상태 저장
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={scrollTop}
					setScrollTop={setScrollTop}
				/>
				<GridCalendar
					rowIndex={targetRowIndex!}
					targetField={targetField!}
					visible={pickerVisible}
					setVisible={(visible) => setPRD2010_R01GridCalendarState({ pickerVisible: visible })}
					value={targetDate}
					pickerPosition={pickerPosition}
					setPickerPosition={(position) => setPRD2010_R01GridCalendarState({ pickerPosition: position })}
					onClear={() => { setPRD2010_R01GridCalendarState({ targetDate: undefined });}}
					onChange={handleGridCalendarChange}
					onClickLoad={(rowIndex, targetField) => {
						const latestList = usePRD2010_R01Store.getState().PRD2010_R01List;
						const foundData = latestList[rowIndex];
						const date = foundData[targetField as keyof PRD2010_R01Dto] as Date;
						if(date) {
							setPRD2010_R01GridCalendarState({ targetDate: new Date(date) });
						}else{
							setPRD2010_R01GridCalendarState({ targetDate: new Date() });
						}
					}}
					onAfterConfirm={afterCalendarConfirmed}
				/>
		</GridContextWrapper>
	)
}
